import React, { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";
import { setSentryUser, clearSentryUser } from "../sentry";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up a new guard (called by owner)
  async function signUpGuard(email, password, profileData) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await setDoc(doc(db, "users", result.user.uid), {
      ...profileData,
      email,
      role: "guard",
      createdAt: serverTimestamp(),
    });
    return result;
  }

  // Sign in
  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Sign out
  async function logout() {
    setUserProfile(null);
    clearSentryUser();
    return signOut(auth);
  }

  // Password reset
  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Load user profile from Firestore
  async function loadUserProfile(uid) {
    const docRef = doc(db, "users", uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const profile = { id: uid, ...docSnap.data() };
      setUserProfile(profile);
      // Tag every Sentry event with this user so triage shows who hit the bug.
      setSentryUser(profile);
      return profile;
    }
    return null;
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await loadUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    login,
    logout,
    signUpGuard,
    resetPassword,
    loadUserProfile,
    isOwner: userProfile?.role === "owner",
    isGuard: userProfile?.role === "guard",
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
